import React, { useMemo, useRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Icon from '../../../../../shared/_TBComponents/Icon';
import SharePlaneIcon from '../SharePlaneIcon';
import NavigatorManageTable, {
  nameSort,
  useActionMenu,
  OverflowNameList,
  ActionMenuWrapper,
  ActionMenuButton,
  ActionMenuContainer,
  ActionMenuDivider,
  ActionMenuItemButton,
  Dash,
} from './NavigatorManageTable';

// Thin wrapper over the shared NavigatorManageTable — supplies only the
// program-specific columns, cells, and bulk bar.

const ProgramNameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
`;

const ProgramNameText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
`;

const AiBadge = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: #77A7FF;
  background: #77A7FF1A;
  border-radius: 2px;
  padding: 1px 6px;
`;

const FlipIconWrap = styled.span`
  display: inline-flex;
  transform: scaleY(-1);
`;

const ProgramNameCell = ({ row }) => {
  const { programName, isAiImport } = row.original;
  return (
    <ProgramNameWrapper>
      <ProgramNameText title={programName}>{programName || 'Untitled program'}</ProgramNameText>
      {isAiImport && <AiBadge>AI</AiBadge>}
    </ProgramNameWrapper>
  );
};

ProgramNameCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      programName: PropTypes.string,
      isAiImport: PropTypes.bool,
    }),
  }).isRequired,
};

const TagsCell = ({ row }) => {
  const { tags, id } = row.original;
  return <OverflowNameList items={tags} idPrefix='program-tags' rowId={id} variant='pill' />;
};

TagsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number,
      tags: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        color: PropTypes.string,
      })),
    }),
  }).isRequired,
};

const NumberCell = ({ value }) => (
  (value || value === 0)
    ? <span>{value}</span>
    : <Dash />
);

NumberCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

NumberCell.defaultProps = {
  value: null,
};

const ProgramActionsDropdown = ({ program: p }) => {
  const {
    isOpen, menuStyle, menuRef, buttonRef, openMenu, close,
  } = useActionMenu();
  return (
    <ActionMenuWrapper onClick={(ev) => ev.stopPropagation()}>
      <ActionMenuButton
        ref={buttonRef}
        type='button'
        onClick={openMenu}
        aria-label='Row actions'
        aria-haspopup='menu'
        aria-expanded={isOpen}
      >
        <Icon icon='dots' size={16} color='currentColor' />
      </ActionMenuButton>
      {isOpen && createPortal(
        <ActionMenuContainer ref={menuRef} style={menuStyle} role='menu'>
          <ActionMenuItemButton type='button' role='menuitem' onClick={() => { close(); p.onBuild(p); }}>
            <Icon icon='build' size={16} color='#1A1A1A' />
            Build
          </ActionMenuItemButton>
          <ActionMenuItemButton type='button' role='menuitem' onClick={() => { close(); p.onEdit(p); }}>
            <Icon icon='edit-workout' size={16} color='#1A1A1A' />
            Edit
          </ActionMenuItemButton>
          <ActionMenuItemButton type='button' role='menuitem' onClick={() => { close(); p.onLoadToCalendar(p); }}>
            <FlipIconWrap>
              <Icon icon='download' size={16} color='#1A1A1A' />
            </FlipIconWrap>
            Load to Calendar
          </ActionMenuItemButton>
          <ActionMenuItemButton type='button' role='menuitem' onClick={() => { close(); p.onShare(p); }}>
            <SharePlaneIcon size={16} color='#1A1A1A' />
            Share
          </ActionMenuItemButton>
          <ActionMenuDivider />
          <ActionMenuItemButton type='button' role='menuitem' onClick={() => { close(); p.onDelete(p); }}>
            <Icon icon='delete' size={16} color='#FF0505' />
            Delete
          </ActionMenuItemButton>
        </ActionMenuContainer>,
        document.body,
      )}
    </ActionMenuWrapper>
  );
};

ProgramActionsDropdown.propTypes = {
  program: PropTypes.shape({
    onBuild: PropTypes.func,
    onEdit: PropTypes.func,
    onLoadToCalendar: PropTypes.func,
    onShare: PropTypes.func,
    onDelete: PropTypes.func,
  }).isRequired,
};

const ActionsCell = ({ row }) => <ProgramActionsDropdown program={row.original} />;

ActionsCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.shape({}) }).isRequired,
};

// Tokenized over the merged name+description+tags string: every
// whitespace-separated term must hit somewhere (see exerciseSearch).
const programSearch = (row, q) => {
  const haystack = [row.programName, row.description, ...(row.tags || []).map((t) => t.name)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return q.split(/\s+/).every((term) => haystack.includes(term));
};

const COLUMNS = [
  {
    Header: 'Program',
    accessor: 'programName',
    sortType: nameSort,
    headerStyle: { minWidth: '260px', flex: '1' },
    cellStyle: { minWidth: '260px', flex: '1' },
    Cell: ProgramNameCell,
  },
  {
    Header: 'Tags',
    id: 'tags',
    accessor: (row) => (row.tags || []).map((t) => t.name).join(' '),
    disableSortBy: true,
    headerStyle: { width: '260px' },
    cellStyle: { width: '260px' },
    Cell: TagsCell,
  },
  {
    Header: 'Weeks',
    accessor: 'numberOfWeeks',
    headerStyle: { width: '110px', textAlign: 'left' },
    cellStyle: { width: '110px', textAlign: 'left' },
    Cell: NumberCell,
  },
  {
    Header: 'Days',
    accessor: 'numberOfDays',
    headerStyle: { width: '110px', textAlign: 'left' },
    cellStyle: { width: '110px', textAlign: 'left' },
    Cell: NumberCell,
  },
  {
    Header: 'Sessions',
    accessor: 'numberOfSessions',
    headerStyle: { width: '110px', textAlign: 'left' },
    cellStyle: { width: '110px', textAlign: 'left' },
    Cell: NumberCell,
  },
  {
    Header: 'Actions',
    id: 'actions',
    sticky: 'right',
    disableSortBy: true,
    headerStyle: { width: '90px', textAlign: 'center' },
    cellStyle: { width: '90px', textAlign: 'center' },
    Cell: ActionsCell,
  },
];

const BULK_ACTIONS = [
  {
    key: 'deletePrograms', label: 'Delete', icon: 'delete', iconColor: '#FF0505',
  },
];

const ManageProgramsTable = React.forwardRef(({
  isLoading, hasError, onRetry, onEditProgram, onBuildProgram, onLoadToCalendarProgram,
  onShareProgram, onDeleteProgram, onBulkDelete, filterControl, bannerContent, tagFilterIds,
}, ref) => {
  const innerRef = useRef(null);
  const savedWorkouts = useSelector((state) => state.calendar.data.savedWorkouts);

  // Lets the parent call scrollToProgram(id) via a ref, from ProgramNavigator's Manage button.
  useImperativeHandle(ref, () => ({
    scrollToProgram: (id) => innerRef.current?.scrollToRow(id),
  }), []);

  const tableData = useMemo(
    () => (savedWorkouts || [])
      // Tag filter is AND semantics: each selected tag narrows the list, so a
      // program must carry every one of them.
      .filter((p) => tagFilterIds.every(
        (id) => (p.tags || []).some((t) => t.id === id),
      ))
      .map((p) => ({
        ...p,
        onBuild: onBuildProgram,
        onEdit: onEditProgram,
        onLoadToCalendar: onLoadToCalendarProgram,
        onShare: onShareProgram,
        onDelete: onDeleteProgram,
      })),
    [
      savedWorkouts, onBuildProgram, onEditProgram, onLoadToCalendarProgram, onShareProgram,
      onDeleteProgram, tagFilterIds,
    ],
  );

  const handleBulkAction = (key, selectedIds) => {
    if (key === 'deletePrograms') onBulkDelete(selectedIds);
  };

  return (
    <NavigatorManageTable
      ref={innerRef}
      data={tableData}
      columns={COLUMNS}
      isLoading={isLoading}
      hasError={hasError}
      errorMessage='Something went wrong loading programs.'
      onRetry={onRetry}
      searchFn={programSearch}
      searchPlaceholder='Search programs and tags'
      emptyMessage='No saved programs yet.'
      searchEmptyMessage='No programs match your search.'
      entityLabel='program'
      defaultSortBy={[{ id: 'programName', desc: false }]}
      bulkActions={BULK_ACTIONS}
      onBulkAction={handleBulkAction}
      extraActionContent={filterControl}
      bannerContent={bannerContent}
      virtualizeRows
    />
  );
});

ManageProgramsTable.displayName = 'ManageProgramsTable';

ManageProgramsTable.propTypes = {
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
  onRetry: PropTypes.func,
  onEditProgram: PropTypes.func,
  onBuildProgram: PropTypes.func,
  onLoadToCalendarProgram: PropTypes.func,
  onShareProgram: PropTypes.func,
  onDeleteProgram: PropTypes.func,
  onBulkDelete: PropTypes.func,
  filterControl: PropTypes.node,
  bannerContent: PropTypes.node,
  tagFilterIds: PropTypes.arrayOf(PropTypes.number),
};

ManageProgramsTable.defaultProps = {
  isLoading: false,
  hasError: false,
  onRetry: () => {},
  onEditProgram: () => {},
  onBuildProgram: () => {},
  onLoadToCalendarProgram: () => {},
  onShareProgram: () => {},
  onDeleteProgram: () => {},
  onBulkDelete: () => {},
  filterControl: null,
  bannerContent: null,
  tagFilterIds: [],
};

export default ManageProgramsTable;
